import React, {createContext, HTMLAttributes, useContext, useMemo} from "react";
import {WpClient} from "../../utils/wp-client";
import once from "lodash/once";

/**
 * This provider uses a new structure to allow passing generic type for the 'others' sub-property and thus make it
 * more flexible instead of doing manual non-enforceable type casts when calling useWpContext. The key to this
 * approach working is the Lodash.once function which ensures the call to `createWpContext` is not executed on every
 * render but only once. Without 'once', it causes the context to reset on every render, thus making its values
 * unusable (all reset to default empty).
 * @source
 * - https://stackoverflow.com/questions/51448291/how-to-create-a-generic-react-component-with-a-typed-context-provider#answer-61020816
 * - https://lodash.com/docs/4.17.15#once
 * - https://lodash.com/docs/4.17.15#before
 * - https://github.com/lodash/lodash/blob/main/src/before.ts
 * - https://github.com/lodash/lodash/blob/main/src/once.ts
 */

// Context Type : Data from WordPress (REST URL, MEDIA URL, etc.) to pass along to React ContextProvider for entire app
export interface WpContextData<T extends ZWindowData> {
    api: WpClient;
    upload: string;
    is_fr: boolean; // simplify code by converting 'lang' to boolean checking for 'fr'
    others: T;
}

// define a function that will execute only once and on subsequent calls to same function return result of 1st call
const createWpContext = once(<T extends ZWindowData, >() => createContext<WpContextData<T>>({
    api: new WpClient(),
    upload: "",
    is_fr: true,
    others: {} as T,
}));
// define hook to access this context from child components - allow passing T type to further define 'others' property;
// this hook calls 'createWpContext' which will execute first time and then only return previous value
export const useWpContext = <T extends ZWindowData>() => useContext(createWpContext<T>());

interface Props extends HTMLAttributes<any> {
    // note: may use Omit<> to exclude less useful param and avoid having to define it later on
    // wp: Omit<WpContextData<ZWindowData>, "others">
    wp: WpContextData<ZWindowData>
}

const WordPressProvider = (props: Props) => {

    // instantiate default initial context only once on load of this component (no re-render since no props change)
    const WpContext = createWpContext<ZWindowData>();

    const contextValue: WpContextData<ZWindowData> = useMemo(() => ({...props.wp}), [props.wp]);
    console.info(`wp.context-value`, contextValue);

    return <WpContext.Provider value={contextValue}>
        {props.children}
    </WpContext.Provider>
}
export default WordPressProvider;
